import { Fragment, memo, useMemo, useRef, useState } from 'react';
import { SignatureActUscVM } from 'civil-status/view-models/step-form';
import { FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { FormikProps } from 'formik';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { DateTime } from 'luxon';
import { Checkbox } from '@mui/material';
import { IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ViewerPDF from 'shell/components/shared/pdf-modal/pdf-viewer';
import { PmiButton } from 'shell/components/shared/button';
import { AttachmentsData } from 'shell/models/act-wizard';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DataCertificateConformity } from 'civil-status/view-models/data-certificate-conformity';
import { AxiosResponse } from 'axios';
import { transformerCertificateConformity } from 'civil-status/utils/transformer-certificate-conformity';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';

interface FormSignatureUSCProps {
  formContext: FormikProps<SignatureActUscVM>;
  isCertificateConformity?: boolean;
  disabledCertificateConformityWithoutAct?: boolean;
  idEvento?: number;
}

const FormSignatureUSCComponent = ({
  formContext,
  isCertificateConformity,
  disabledCertificateConformityWithoutAct = false,
  idEvento,
}: FormSignatureUSCProps): React.ReactElement => {
  const reference = useRef<HTMLDivElement | null>(null);
  const [isVisibility, setIsVisibility] = useState<boolean>(false);

  const values: SignatureActUscVM = useMemo(() => {
    return formContext.values || {};
  }, [formContext.values]);

  const onVisibilityPress = useEventCallback(() => {
    setIsVisibility(!isVisibility);
  });

  const onChangeListDocuments = useEventCallback((idAllegato?: number) => {
    if (!idAllegato) {
      return;
    }

    if (values.documentsSelected?.includes(idAllegato)) {
      // eliminare id dalla lista
      formContext.setValues({
        ...values,
        documentsSelected: values.documentsSelected?.filter((el) => el !== idAllegato),
        document: undefined,
      });
      return;
    }

    const arrCopy = (values.documentsSelected || [])?.slice();

    // aggiungere id alla lista
    arrCopy?.push(idAllegato);
    formContext.setValues({
      ...values,
      documentsSelected: arrCopy,
      document: undefined,
    });
  });

  const disabledCertificateConformity = useMemo(() => {
    return (values.documentsSelected || [])?.length === 0;
  }, [values.documentsSelected]);

  const saveDocuments = useHttpClient(`${__ACT_API_URL__}/allegato-atti/saveFlagAllegatiList`, {
    options: {
      method: 'POST',
      errorCatching: true,
      loading: true,
    },
  });

  const getDataCertificateConformity = useHttpClient(
    `${__ACT_API_URL__}/evento/anteprima/attestazione/${idEvento}`,
    {
      options: {
        method: 'GET',
        errorCatching: true,
        loading: true,
      },
    }
  );

  const onCertificateConformity = useEventCallback(() => {
    const data: Array<AttachmentsData> = (values.allegatoList || []).map((itemAllegato) => {
      const res = {
        key: itemAllegato.key,
        idEvento,
        idAnscAllegato: itemAllegato.idAnscAllegato,
        tipoAllegato: itemAllegato.tipoAllegato,
      };

      if (
        itemAllegato.key?.idAllegato &&
        !values.documentsSelected?.includes(itemAllegato.key?.idAllegato)
      ) {
        // se non selezionato questo idAllegato
        return {
          ...res,
          flagAttestazione: '0',
          flagInclusoAttestazioneConformita: '0',
        };
      }

      // se selezionato questo idAllegato
      return {
        ...res,
        flagAttestazione: '1',
        flagInclusoAttestazioneConformita: '1',
      };
    });

    (saveDocuments({ data }) as ResponsePromise<unknown, unknown>).then(() => {
      (
        getDataCertificateConformity() as unknown as ResponsePromise<
          HttpResponse<DataCertificateConformity>,
          HttpResponse<DataCertificateConformity>
        >
      ).then((res) => {
        const castedRes = transformerCertificateConformity(
          res as AxiosResponse<HttpResponse<DataCertificateConformity>>
        ) as SignatureActUscVM;

        formContext.setValues({
          ...values,
          ...(castedRes || {}),
        });
      });
    });
  });

  return (
    <Grid item container spacing={2} xs={12}>
      {values.allegatoList?.map((itemAllegato, index) => (
        <Grid item xs={12} key={itemAllegato.descrizioneTipoAllegato}>
          <FormControlLabel
            control={
              <Checkbox
                id={`${itemAllegato.descrizioneTipoAllegato}-${index}`}
                checked={
                  itemAllegato.key?.idAllegato
                    ? values.documentsSelected?.includes(itemAllegato.key?.idAllegato)
                    : false
                }
                onChange={() => onChangeListDocuments(itemAllegato.key?.idAllegato)}
                inputProps={{ 'aria-label': itemAllegato.descrizioneTipoAllegato }}
              />
            }
            label={itemAllegato.descrizioneTipoAllegato}
          />
        </Grid>
      ))}
      {isCertificateConformity ? (
        <Grid item xs={12}>
          <PmiButton
            color="cdmBrandThird"
            variant="outlined"
            onClick={() => onCertificateConformity()}
            disabled={disabledCertificateConformity}
          >
            Genera attestazione di conformità
          </PmiButton>
        </Grid>
      ) : undefined}
      {isCertificateConformity && values.document?.raw ? (
        <Grid item xs={12} display="flex" justifyContent="center">
          <ViewerPDF
            data={values.document.raw}
            name={values.document.name}
            downloadable
            printable
            fixedDimension={reference.current?.clientWidth}
            positionControls="static"
          />
        </Grid>
      ) : undefined}
      {isCertificateConformity ? undefined : (
        <Fragment>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isAnagrafica"
                  checked={values.isAnagrafica || false}
                  onChange={() => {
                    formContext.setValues({
                      ...values,
                      isAnagrafica: !values.isAnagrafica,
                      dateValidity: !values.isAnagrafica ? values.dateValidity : DateTime.now(),
                    });
                  }}
                  inputProps={{ 'aria-label': 'Comunicazione anagrafica' }}
                />
              }
              label="Comunicazione anagrafica"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DatePicker
              label={`Data validità evento ${!values.isAnagrafica ? '' : '*'}`}
              aria-label="Data validità evento"
              value={values.dateValidity || null}
              disabled={!values.isAnagrafica}
              onChange={(date) => formContext.setFieldValue('dateValidity', date || null)}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="birthDate"
                  fullWidth
                  error={
                    formContext.touched?.dateValidity && Boolean(formContext.errors?.dateValidity)
                  }
                  helperText={formContext.errors?.dateValidity}
                />
              )}
            />
          </Grid>
        </Fragment>
      )}
      <Grid item container spacing={2} xs={12}>
        <Grid item xs={12} md={4}>
          <PmiTextField
            aria-label="Utenza"
            label="Utenza *"
            id="user"
            name="user"
            fullWidth
            type="text"
            variant="outlined"
            value={values.user || ''}
            onChange={(event) => formContext.setFieldValue('user', event.target.value || '')}
            error={formContext.touched?.user && Boolean(formContext.errors?.user)}
            helperText={formContext.errors?.user}
            disabled={disabledCertificateConformityWithoutAct}
          />
        </Grid>
        <Grid item xs={12} md={4} display="flex">
          <span style={{ width: '85%' }}>
            <PmiTextField
              aria-label="PIN"
              label="PIN *"
              id="pin"
              name="pin"
              fullWidth
              type={isVisibility ? 'text' : 'password'}
              variant="outlined"
              value={values.pin || ''}
              onChange={(event) => formContext.setFieldValue('pin', event.target.value || '')}
              error={formContext.touched?.pin && Boolean(formContext.errors?.pin)}
              helperText={formContext.errors?.pin}
              disabled={disabledCertificateConformityWithoutAct}
            />
          </span>
          <span style={{ paddingLeft: '10px', width: '15%' }}>
            <IconButton
              aria-label="visualizza/nascondi PIN"
              component="span"
              onClick={() => onVisibilityPress()}
            >
              {isVisibility ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </span>
        </Grid>
        <Grid item xs={12} md={4}>
          <PmiTextField
            aria-label="OTP"
            label="OTP"
            id="otp"
            name="otp"
            fullWidth
            type="text"
            variant="outlined"
            value={values.otp || ''}
            onChange={(event) => formContext.setFieldValue('otp', event.target.value || '')}
            error={formContext.touched?.otp && Boolean(formContext.errors?.otp)}
            helperText={formContext.errors?.otp}
            disabled={disabledCertificateConformityWithoutAct}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

FormSignatureUSCComponent.displayName = 'Form Signature USC component';

const FormSignatureUSC = memo(FormSignatureUSCComponent);

export default FormSignatureUSC;
